import { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { Calendar, dateFnsLocalizer, type View, type HeaderProps, type DayLayoutFunction } from 'react-big-calendar'
import withDragAndDrop, { type EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop'
import overlap from 'react-big-calendar/lib/utils/layout-algorithms/overlap'
import { format, parse, startOfWeek, getDay, endOfDay, startOfDay, addDays } from 'date-fns'
import { TZDate } from '@date-fns/tz'
import { enUS } from 'date-fns/locale/en-US'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { usePlanningWeek } from '@/context/planning-week-context'
import { useSettings } from '@/context/settings-context'
import { Loader2, Trash2, Check, ExternalLink } from 'lucide-react'

export const GOOGLE_CALENDAR_COLORS: Record<string, { hex: string; name: string }> = {
  '0':  { hex: '#808080', name: 'Default' },
  '1':  { hex: '#7986cb', name: 'Lavender' },
  '2':  { hex: '#33b679', name: 'Sage' },
  '3':  { hex: '#8e24aa', name: 'Grape' },
  '4':  { hex: '#e67c73', name: 'Flamingo' },
  '5':  { hex: '#f6bf26', name: 'Banana' },
  '6':  { hex: '#f4511e', name: 'Tangerine' },
  '7':  { hex: '#039be5', name: 'Peacock' },
  '8':  { hex: '#616161', name: 'Graphite' },
  '9':  { hex: '#3f51b5', name: 'Blueberry' },
  '10': { hex: '#0b8043', name: 'Basil' },
  '11': { hex: '#d50000', name: 'Tomato' },
}

export interface ColorOption {
  colorId: string
  hex: string
  colorName: string
  isIgnored: boolean
  lifeDomainTitle: string | null
  labelName: string | null
}

export type ColorMappings = Record<string, ColorOption[]>

export interface PlanningEvent {
  id: string
  title: string
  start: Date
  end: Date
  allDay: boolean
  color?: string
  htmlLink?: string
  status?: string
  integrationsCalendarId?: string
  eventColorId?: string | null
}

export interface EventUpdatePayload {
  start?: Date
  end?: Date
  title?: string
  colorId?: string
  colorHex?: string
}

export interface PlanningCalendarProps {
  events?: PlanningEvent[]
  loading?: boolean
  editable?: boolean
  colorMappings?: ColorMappings
  onEventUpdate?: (id: string, updates: EventUpdatePayload) => void
  onEventDelete?: (id: string) => void
}

const locales = { 'en-US': enUS }

function buildLocalizer(weekStartsOnNumber: 0 | 1) {
  return dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: weekStartsOnNumber }),
    getDay,
    locales,
  })
}

const CALENDAR_FORMATS = {
  timeGutterFormat: (date: Date, culture?: string, localizer?: { format: (d: Date, f: string, c?: string) => string }) =>
    localizer?.format(date, 'h a', culture) ?? '',
  eventTimeRangeFormat: () => '',
}

function DayHeader({ date }: HeaderProps) {
  const dayName = format(date, 'EEE')
  const dayNum = format(date, 'd')
  return (
    <div className="flex flex-col items-center leading-tight">
      <span className="text-[0.7rem] uppercase tracking-wide">{dayName}</span>
      <span className="text-base font-semibold">{dayNum}</span>
    </div>
  )
}

const CUSTOM_COMPONENTS = {
  header: DayHeader,
}

const EVENT_PROP_GETTER = (event: PlanningEvent) => {
  const bg = event.color || undefined
  return {
    style: {
      backgroundColor: bg,
      borderColor: 'var(--background)',
      opacity: event.status === 'tentative' ? 0.65 : 1,
    },
  }
}

const DnDCalendar = withDragAndDrop<PlanningEvent>(Calendar)

const SEGMENT_ID_PREFIX = '__seg_'

/** Extract original event ID from a segment ID (e.g. "abc123__seg_0" -> "abc123") */
function getOriginalEventId(segmentOrOriginalId: string): string {
  const idx = segmentOrOriginalId.indexOf(SEGMENT_ID_PREFIX)
  return idx >= 0 ? segmentOrOriginalId.slice(0, idx) : segmentOrOriginalId
}

/**
 * Splits multi-day events into daily segments for display.
 * react-big-calendar's week view does not reliably display events spanning across midnight.
 */
function splitMultiDayEventsForDisplay(
  events: PlanningEvent[],
  timezone: string
): PlanningEvent[] {
  const result: PlanningEvent[] = []

  for (const event of events) {
    const startTz = new TZDate(event.start, timezone)
    const endTz = new TZDate(event.end, timezone)

    const startDayStart = startOfDay(startTz)
    const endDayStart = startOfDay(endTz)

    if (startDayStart.getTime() === endDayStart.getTime()) {
      result.push(event)
      continue
    }

    let i = 0
    let currentDayStart = startDayStart
    while (currentDayStart.getTime() <= endDayStart.getTime()) {
      const currentDayEnd = endOfDay(currentDayStart)
      const segStart = Math.max(event.start.getTime(), currentDayStart.getTime())
      const segEnd = Math.min(event.end.getTime(), currentDayEnd.getTime())

      if (segEnd > segStart) {
        result.push({
          ...event,
          id: `${event.id}${SEGMENT_ID_PREFIX}${i}`,
          start: new Date(segStart),
          end: new Date(segEnd),
        })
      }
      currentDayStart = addDays(currentDayStart, 1)
      i++
    }
  }

  return result
}

function toLocalDatetimeValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function EventEditPopover({
  event,
  open,
  onClose,
  onUpdate,
  onDelete,
  anchorRect,
  colorOptions,
}: {
  event: PlanningEvent
  open: boolean
  onClose: () => void
  onUpdate: (id: string, updates: EventUpdatePayload) => void
  onDelete: (id: string) => void
  anchorRect: { top: number; left: number } | null
  colorOptions?: ColorOption[]
}) {
  const [title, setTitle] = useState(event.title)
  const [startStr, setStartStr] = useState(toLocalDatetimeValue(event.start))
  const [endStr, setEndStr] = useState(toLocalDatetimeValue(event.end))
  const [selectedColorId, setSelectedColorId] = useState(event.eventColorId ?? '0')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setTitle(event.title)
    setStartStr(toLocalDatetimeValue(event.start))
    setEndStr(toLocalDatetimeValue(event.end))
    setSelectedColorId(event.eventColorId ?? '0')
  }, [event.title, event.start, event.end, event.eventColorId])

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.select(), 50)
    }
  }, [open])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }

  const handleSave = () => {
    const updates: EventUpdatePayload = {}

    if (title !== event.title) updates.title = title

    const parsedStart = new Date(startStr)
    if (!isNaN(parsedStart.getTime()) && parsedStart.getTime() !== event.start.getTime()) {
      updates.start = parsedStart
    }

    const parsedEnd = new Date(endStr)
    if (!isNaN(parsedEnd.getTime()) && parsedEnd.getTime() !== event.end.getTime()) {
      updates.end = parsedEnd
    }

    const origColorId = event.eventColorId ?? '0'
    if (selectedColorId !== origColorId) {
      updates.colorId = selectedColorId
      const entry = GOOGLE_CALENDAR_COLORS[selectedColorId]
      if (entry) updates.colorHex = entry.hex
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(event.id, updates)
    }

    onClose()
  }

  if (!open || !anchorRect) return null

  return (
    <Popover open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <PopoverTrigger asChild>
        <span
          className="fixed pointer-events-none"
          style={{ top: anchorRect.top, left: anchorRect.left, width: 1, height: 1 }}
        />
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" side="right" align="start">
        <div className="space-y-3">
          {/* Title */}
          <Input
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 text-sm"
          />

          {/* Original event link */}
          {event.htmlLink && (
            <a
              href={event.htmlLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              Open original event
            </a>
          )}

          {/* Start / End datetime pickers */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Start</label>
            <Input
              type="datetime-local"
              value={startStr}
              onChange={(e) => setStartStr(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">End</label>
            <Input
              type="datetime-local"
              value={endStr}
              onChange={(e) => setEndStr(e.target.value)}
              className="h-8 text-xs"
            />
          </div>

          {/* Color dropdown */}
          {colorOptions && colorOptions.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Color</label>
              <Select value={selectedColorId} onValueChange={setSelectedColorId}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colorOptions.map((opt) => (
                    <SelectItem key={opt.colorId} value={opt.colorId}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 shrink-0 rounded-full border border-border"
                          style={{ backgroundColor: opt.hex }}
                        />
                        <span className="flex flex-col leading-tight">
                          <span>{opt.lifeDomainTitle || '\u00A0'}</span>
                          {opt.labelName && (
                            <span className="text-[10px] text-muted-foreground">
                              {opt.labelName}
                            </span>
                          )}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 gap-1.5 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => { onDelete(event.id); onClose() }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
            <Button
              size="sm"
              className="flex-1 gap-1.5"
              onClick={handleSave}
            >
              <Check className="h-3.5 w-3.5" />
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function PlanningCalendar({
  events = [],
  loading = false,
  editable = false,
  colorMappings,
  onEventUpdate,
  onEventDelete,
}: PlanningCalendarProps) {
  const { currentWeekStart } = usePlanningWeek()
  const { weekStartsOn, timezone } = useSettings()
  const [selectedEvent, setSelectedEvent] = useState<PlanningEvent | null>(null)
  const [popoverAnchor, setPopoverAnchor] = useState<{ top: number; left: number } | null>(null)

  const weekStartsOnNumber: 0 | 1 = weekStartsOn === 'monday' ? 1 : 0

  const localizer = useMemo(
    () => buildLocalizer(weekStartsOnNumber),
    [weekStartsOnNumber],
  )

  const displayEvents = useMemo(
    () => splitMultiDayEventsForDisplay(events, timezone),
    [events, timezone],
  )

  const eventById = useMemo(() => {
    const map = new Map<string, PlanningEvent>()
    for (const e of events) map.set(e.id, e)
    return map
  }, [events])

  const handleSelectEvent = useCallback((event: PlanningEvent, e: React.SyntheticEvent) => {
    if (editable && onEventUpdate && onEventDelete) {
      const nativeEvent = e.nativeEvent as MouseEvent
      setPopoverAnchor({ top: nativeEvent.clientY, left: nativeEvent.clientX })
      const originalId = getOriginalEventId(event.id)
      const originalEvent = eventById.get(originalId) ?? event
      setSelectedEvent(originalEvent)
    } else if (event.htmlLink) {
      window.open(event.htmlLink, '_blank', 'noopener,noreferrer')
    }
  }, [editable, onEventUpdate, onEventDelete, eventById])

  const handleEventDrop = useCallback((args: EventInteractionArgs<PlanningEvent>) => {
    if (!onEventUpdate || !args.start || !args.end) return
    const originalId = getOriginalEventId(args.event.id)
    const originalEvent = eventById.get(originalId)
    if (originalEvent) {
      const delta = new Date(args.start).getTime() - args.event.start.getTime()
      onEventUpdate(originalId, {
        start: new Date(originalEvent.start.getTime() + delta),
        end: new Date(originalEvent.end.getTime() + delta),
      })
    } else {
      onEventUpdate(args.event.id, {
        start: new Date(args.start),
        end: new Date(args.end),
      })
    }
  }, [onEventUpdate, eventById])

  const handleEventResize = useCallback((args: EventInteractionArgs<PlanningEvent>) => {
    if (!onEventUpdate || !args.start || !args.end) return
    const originalId = getOriginalEventId(args.event.id)
    const originalEvent = eventById.get(originalId)
    if (originalEvent) {
      const segMatch = args.event.id.match(new RegExp(`${SEGMENT_ID_PREFIX}(\\d+)$`))
      const segmentIndex = segMatch ? parseInt(segMatch[1], 10) : -1
      if (segmentIndex === 0) {
        onEventUpdate(originalId, { start: new Date(args.start) })
      } else if (segmentIndex > 0) {
        onEventUpdate(originalId, { end: new Date(args.end) })
      } else {
        onEventUpdate(args.event.id, {
          start: new Date(args.start),
          end: new Date(args.end),
        })
      }
    } else {
      onEventUpdate(args.event.id, {
        start: new Date(args.start),
        end: new Date(args.end),
      })
    }
  }, [onEventUpdate, eventById])

  const defaultView: View = 'week'

  const calendarProps = {
    localizer,
    events: displayEvents,
    date: currentWeekStart,
    view: defaultView,
    views: ['week'] as View[],
    toolbar: false,
    onNavigate: () => {},
    onView: () => {},
    onSelectEvent: handleSelectEvent,
    eventPropGetter: EVENT_PROP_GETTER,
    components: CUSTOM_COMPONENTS,
    formats: CALENDAR_FORMATS,
    startAccessor: 'start' as const,
    endAccessor: 'end' as const,
    allDayAccessor: 'allDay' as const,
    titleAccessor: 'title' as const,
    tooltipAccessor: 'title' as const,
    style: { height: '100%' },
    step: 15,
    timeslots: 4,
    dayLayoutAlgorithm: ((params) =>
      overlap({ ...params, minimumStartDifference: 15 })) as DayLayoutFunction<PlanningEvent>,
  }

  return (
    <Card className="planning-calendar-wrapper flex h-full min-h-0 flex-col overflow-hidden p-2 md:p-4">
      {loading && events.length === 0 && (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      <div className={loading && events.length === 0 ? 'hidden' : 'min-h-0 flex-1'}>
        {editable ? (
          <DnDCalendar
            {...calendarProps}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            resizable
            selectable={false}
          />
        ) : (
          <Calendar<PlanningEvent>
            {...calendarProps}
          />
        )}
      </div>

      {selectedEvent && onEventUpdate && onEventDelete && (
        <EventEditPopover
          event={selectedEvent}
          open={!!selectedEvent}
          onClose={() => { setSelectedEvent(null); setPopoverAnchor(null) }}
          onUpdate={onEventUpdate}
          onDelete={onEventDelete}
          anchorRect={popoverAnchor}
          colorOptions={
            selectedEvent.integrationsCalendarId && colorMappings
              ? colorMappings[selectedEvent.integrationsCalendarId]
              : undefined
          }
        />
      )}
    </Card>
  )
}
